'use client';

import type { PostType } from '@prisma/client';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import type { RouterOutputs } from '@/server/callers/client';
import { api } from '@/server/callers/client';
import { trackEvent } from '@/utils/analytics';
import { POSTS_PAGE_SIZE } from '@/utils/constants';
import { getFileUrl, getPostUrl } from '@/utils/helpers';
import { usePageViewTracking } from '@/utils/hooks';

// Format date helper
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

type PostsData = RouterOutputs['posts']['infinity'];
type PostItem = PostsData['data'][0];

interface PostsListProps {
  postType?: PostType;
  initialData?: PostsData;
}

export default function PostsList({ postType, initialData }: PostsListProps) {
  // Track client page views
  usePageViewTracking();

  const { ref: loadMoreRef, inView } = useInView();
  const [posts, setPosts] = useState<PostsData['data']>(initialData?.data || []);
  const [page, setPage] = useState(1);
  const prevPageRef = useRef(page);
  const isLoadingMore = useRef(false);

  // Use query for posts with explicit refetch
  const postsQuery = api.posts.infinity.useQuery(
    {
      pagination: {
        page: page,
        pageSize: POSTS_PAGE_SIZE,
      },
      filters: {
        search: '',
        type: postType,
      },
      sorting: {
        column: 'createdAt',
        direction: 'desc',
      },
    },
    {
      initialData: initialData,
      // This ensures we don't automatically refetch when page changes,
      // as we'll manually call refetch
      enabled: true,
    },
  );

  // Initialize posts with initial data
  useEffect(() => {
    if (initialData && posts.length === 0) {
      setPosts(initialData.data);
    }
  }, [initialData, posts.length]);

  // Effect to handle page change
  useEffect(() => {
    if (page !== prevPageRef.current) {
      prevPageRef.current = page;

      // Explicit refetch when page changes
      postsQuery.refetch().then((result) => {
        if (result.data) {
          if (page === 1) {
            // For first page, replace posts
            setPosts(result.data.data);
          } else {
            // For subsequent pages, append posts
            setPosts((prevPosts) => {
              const existingIds = new Set(prevPosts.map((post) => post.id));
              const newPosts = result.data.data.filter(
                (post) => !existingIds.has(post.id),
              );

              return [...prevPosts, ...newPosts];
            });
          }
        }

        isLoadingMore.current = false;
      });
    }
  }, [page, postsQuery]);

  // Load more posts when the load more element comes into view
  useEffect(() => {
    if (
      inView &&
      postsQuery.data?.hasNextPage &&
      !isLoadingMore.current &&
      !postsQuery.isFetching
    ) {
      isLoadingMore.current = true;

      // Track pagination event
      trackEvent({
        action: 'posts_pagination',
        category: 'content',
        label: `Load page ${page + 1} - ${postType || 'all posts'}`,
        value: page + 1,
      });

      // Increment page which will trigger the page change effect
      setPage((prevPage) => prevPage + 1);
    }
  }, [inView, postsQuery.data?.hasNextPage, page, postType, postsQuery.isFetching]);

  const handlePostClick = (post: PostItem) => {
    trackEvent({
      action: 'post_click',
      category: 'content',
      label: post.title,
      value: 1,
    });
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post) => {
          return (
            <div
              key={post.id}
              className="bg-white rounded-4xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <Link
                href={getPostUrl(post.slug, post.type)}
                className="block"
                onClick={() => handlePostClick(post)}
              >
                <div className="relative w-full aspect-[2/1]">
                  <Image
                    src={getFileUrl(post.cover?.key, 'image')}
                    alt={post.title}
                    fill
                    className="object-cover"
                  />
                </div>

                <div className="p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium bg-gray-100 px-3 py-1 rounded-full">
                      {post.type}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(post.createdAt)}
                    </span>
                  </div>

                  <h3 className="text-lg font-medium line-clamp-2 mb-2">
                    {post.title}
                  </h3>

                  <div className="flex justify-end">
                    <span className="text-sm text-blue-600 font-medium">
                      Read more →
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          );
        })}
      </div>

      {/* Loading indicator and load more reference */}
      <div ref={loadMoreRef} className="w-full flex justify-center my-8">
        {postsQuery.isFetching && (
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
        )}

        {!postsQuery.data?.hasNextPage &&
          !postsQuery.isFetching &&
          posts.length > 0 && (
            <div className="text-center text-gray-500">
              You've reached the end of our {postType?.toLowerCase() || 'posts'}.
              Check back soon for more!
            </div>
          )}

        {!postsQuery.isFetching && posts.length === 0 && (
          <div className="text-center text-gray-500">
            No {postType?.toLowerCase() || 'posts'} found. Check back soon!
          </div>
        )}
      </div>
    </div>
  );
}
